"use client";

import { useState, useEffect, useRef, cloneElement, ReactElement, FunctionComponent, ReactNode, KeyboardEvent, MouseEvent } from "react";
import { useLayer, useHover, mergeRefs } from "react-laag";
import { isReactText } from "../utils/helper";
import { PlacementType } from "react-laag/dist/PlacementType";
import { TailwindColors } from "../types/general";
import { Body, Wrapper, Arrow } from "./tooltip.styles";
import styled from "styled-components";

export interface TooltipProps {
    /**
     * Children of the tooltip.
     */
    children: ReactNode;

    /**
     * Delay in ms on enter.
     */
    delayEnter?: number;

    /**
     * Delay in ms on leave.
     */
    delayLeave?: number;

    /**
     * Where the tooltip will be opend.
     */
    placement?: PlacementType;

    /**
     * Color of the tooltip.
     */
    color?: keyof TailwindColors | undefined;

    /**
     * The tooltip will stay open when hovering the content.
     */
    keepOpen?: boolean | false;

    /**
     * Content of the tooltip.
     */
    text?: ReactNode;

    /**
     * Theme of the tooltip.
     */
    theme?: "default" | "standalone";

    /**
     * Z-Index position of the tooltip.
     */
    zIndex?: number;

    /**
     * Class name of the tooltip.
     */
    className?: string;
}

export const Tooltip: FunctionComponent<TooltipProps> = ({
    delayEnter = 0,
    delayLeave = 0,
    color,
    placement,
    children,
    text,
    keepOpen,
    theme,
    zIndex,
    className,
}) => {
    const [libraryLoaded, setLibraryLoaded] = useState<boolean>(false);

    useEffect(() => {
        const loadLibrary = async () => {
            await import("react-laag");
            setLibraryLoaded(true);
        };
        loadLibrary();
    }, []);

    if (!libraryLoaded || !text) {
        return <>{children}</>;
    }

    return (
        <TooltipBody
            delayEnter={delayEnter}
            delayLeave={delayLeave}
            color={color}
            placement={placement}
            text={text}
            keepOpen={keepOpen}
            theme={theme}
            zIndex={zIndex}
            className={className}
        >
            {children}
        </TooltipBody>
    );
};

export const TooltipBody: FunctionComponent<TooltipProps> = ({
    delayEnter = 0,
    delayLeave = 0,
    color,
    placement,
    children,
    text,
    keepOpen,
    zIndex,
    theme,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [ReactLaag, setReactLaag] = useState<any>(null);
    const [FramerMotion, setFramerMotion] = useState<any>(null);
    const [isOver, hoverProps] = useHover({ delayEnter: delayEnter, delayLeave: delayLeave });
    const { triggerProps, layerProps, arrowProps, renderLayer } = useLayer({
        isOpen: isOver,
        placement: placement || "top-center",
        triggerOffset: 14,
        auto: true,
    });
    let trigger: ReactNode;

    useEffect(() => {
        import("framer-motion").then((module) => {
            setFramerMotion(module);
        });
        import("react-laag").then((module) => {
            setReactLaag(module);
        });
    }, []);

    if (!FramerMotion) {
        return null;
    }

    const keyUpHandler = (event: KeyboardEvent<HTMLElement>) => {
        if (event.code === "Tab") {
            hoverProps.onMouseEnter(event);
        }
    };

    const keyDownHandler = (event: KeyboardEvent<HTMLElement>) => {
        if (event.code === "Tab") {
            hoverProps.onMouseLeave(event);
        }
    };

    const mouseLeaveHandler = (event: MouseEvent<HTMLElement>) => {
        const { relatedTarget } = event;
        if (!(relatedTarget instanceof Element && ref.current?.contains(relatedTarget))) {
            hoverProps.onMouseLeave(event);
        }
    };

    if (isReactText(children)) {
        trigger = (
            <span
                {...triggerProps}
                {...hoverProps}
                onKeyDown={keyDownHandler}
                onKeyUp={keyUpHandler}
                onMouseLeave={mouseLeaveHandler}
                aria-describedby="tooltip-layer"
            >
                {children}
            </span>
        );
    } else {
        trigger = cloneElement(children as ReactElement, {
            ...triggerProps,
            ...hoverProps,
            onKeyDown: keyDownHandler,
            onKeyUp: keyUpHandler,
            onMouseLeave: mouseLeaveHandler,
            "aria-describedby": "tooltip-layer",
        });
    }

    const { motion, AnimatePresence } = FramerMotion;
    const { Arrow: ArrowLaag } = ReactLaag;
    const StyledArrow = styled(ArrowLaag)`
        ${Arrow}
    `;

    return (
        <>
            {trigger}
            {renderLayer(
                <AnimatePresence>
                    {isOver && (
                        <motion.div
                            id="tooltip-layer"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.18 }}
                            keepOpen={keepOpen}
                            {...layerProps}
                            ref={mergeRefs(layerProps.ref, ref)}
                            onMouseLeave={hoverProps.onMouseLeave}
                            style={{ ...layerProps.style, zIndex: zIndex || 13000, pointerEvents: keepOpen ? "auto" : "none" }}
                        >
                            <Wrapper color={color} keepOpen={keepOpen} layoutTheme={theme}>
                                <Body color={color}>{text}</Body>
                                {theme !== "standalone" && (
                                    <StyledArrow {...arrowProps} borderWidth={1} roundness={2} borderColor="#e3e6ea" size={8} />
                                )}
                            </Wrapper>
                        </motion.div>
                    )}
                </AnimatePresence>,
            )}
        </>
    );
};
